import type { ConsoleUser, PlatformUser, PlatformUserApiResponse } from '../../stores/auth'
import type { BillingStatus, Plan, SubscriptionState } from '../../types/subscription'
import { periodToBillingCycle, subscriptionStateToStatus } from '../../utils/subscriptionHelpers'
import { ownerApiCall } from '../axiosInstance'
import { demoApi } from './db'
import { mapResponseToPlatformUser } from './usersApi'

export const billingApi = {
	getPlans: async (organizationId: string): Promise<Plan[]> => {
		try {
			const res = await demoApi.getNoTenant<{ plans: Plan[] }>('/billing/plans')
			if (res.plans?.length > 0) {
				return res.plans.map(p => ({
					id: p.id,
					name: p.name,
					description: p.description,
					monthly_price: p.monthly_price,
					yearly_price: p.yearly_price,
					quota: p.quota,
				})) as Plan[]
			}
		} catch (error) {
			console.error('Failed to fetch plans from demo API:', error)
		}

		const response = await ownerApiCall<{ result: Plan[] }>(
			`/v1/organizations/${encodeURIComponent(organizationId)}/plans`,
		)
		return response.result || []
	},
	getPublicPlans: async (): Promise<Plan[]> => {
		try {
			const res = await demoApi.getNoTenant<{ plans: Plan[] }>('/billing/plans')
			if (res.plans.length > 0) {
				return res.plans.map(p => ({
					id: p.id,
					name: p.name,
					description: p.description,
					monthly_price: p.monthly_price,
					yearly_price: p.yearly_price,
					quota: p.quota,
				})) as Plan[]
			}
		} catch (error) {
			console.error('Failed to fetch public plans from demo API:', error)
		}

		const response = await ownerApiCall<{ result: Plan[] }>('/v1/plans')
		return response.result || []
	},
	getCurrentUserWithSubscription: async (): Promise<ConsoleUser | PlatformUser> => {
		const response = await demoApi.getNoTenant<PlatformUserApiResponse>('/me')
		return mapResponseToPlatformUser(response)
	},
	getBillingStatusFromUser: (
		user: ConsoleUser | PlatformUser,
		organizationId?: string | null,
	): BillingStatus | null => {
		if ('tenants' in user) {
			const organization = organizationId
				? user.organizations?.find(org => org.id === organizationId) || user.organizations?.[0]
				: user.organizations?.[0]

			if (!organization) {
				return null
			}

			const subscription = organization.subscription

			return {
				email: user.email,
				current_plan: subscription?.plan_name || '',
				billing_frequency: periodToBillingCycle(subscription?.period as 'Yearly' | 'Monthly') || 'monthly',
				subscription_status: subscriptionStateToStatus(subscription?.state as SubscriptionState),
				subscription_state: subscription?.state as SubscriptionState,
				next_billing_date: subscription?.expires_at,
				payment_method_status: subscription?.state === 'pending_initial_payment' ? 'pending' : 'active',
			}
		}

		const organization = organizationId
			? user.organizations?.find(org => org.id === organizationId) || user.organizations?.[0]
			: user.organizations?.[0]

		const subscription = organization?.subscription

		if (!subscription) {
			return null
		}

		return {
			email: user.email,
			current_plan: subscription.plan_name || '',
			billing_frequency: periodToBillingCycle(subscription.period as 'Yearly' | 'Monthly'),
			subscription_status: subscriptionStateToStatus(subscription.state as SubscriptionState),
			subscription_state: subscription.state as SubscriptionState,
			next_billing_date: subscription.expires_at,
			payment_method_status: subscription.state === 'pending_initial_payment' ? 'pending' : 'active',
		}
	},
}
